import { TFunction } from "i18next";
import { z } from "zod";

// Sentinel value used in <select> elements to represent "All" (which maps to
// null on the wire). Empty string would also work but is more error-prone.
export const APP_NOTICE_ALL_VALUE = "ALL";

// Schema for the Announcement create/edit form.
// `environment` and `country` use the sentinel "ALL" to mean "no scope" and are
// later converted to null in the page before dispatching the saga.
export const appNoticeRuleSchema = (t: TFunction) =>
  z
    .object({
      environment: z.string().min(1, t("validation.required")),
      country: z.string().min(1, t("validation.required")),
      enabled: z.boolean(),
      validFrom: z.string(),
      validUntil: z.string(),
      messageEn: z.string().max(500).optional().or(z.literal("")),
      messageFr: z.string().max(500).optional().or(z.literal("")),
      messageAr: z.string().max(500).optional().or(z.literal("")),
      bgColor: z.string().max(20).optional().or(z.literal("")),
    })
    .superRefine((data, ctx) => {
      // Both date fields are optional individually but when both are provided
      // we enforce that `valid_until` is strictly greater than `valid_from`.
      if (data.validFrom && data.validUntil) {
        const from = new Date(data.validFrom).getTime();
        const until = new Date(data.validUntil).getTime();
        if (Number.isFinite(from) && Number.isFinite(until) && until <= from) {
          ctx.addIssue({
            code: "custom",
            path: ["validUntil"],
            message: t("admin.governance.validation.untilAfterFrom"),
          });
        }
      }

      // Whenever `valid_until` is set, it must be in the future.
      if (data.validUntil) {
        const until = new Date(data.validUntil).getTime();
        if (Number.isFinite(until) && until <= Date.now()) {
          ctx.addIssue({
            code: "custom",
            path: ["validUntil"],
            message: t("admin.governance.validation.untilInFuture"),
          });
        }
      }
    });

export type AppNoticeRuleFormData = z.infer<
  ReturnType<typeof appNoticeRuleSchema>
>;
